"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.TitlesSection = void 0;
require("./TitlesSection.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _Icon = _interopRequireDefault(require("../../components/Icon/Icon"));
var _renderObject = require("../../util/renderObject/renderObject");
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * TitlesSection Component
 *
 * A layout component for displaying a heading section with an optional icon,
 * subheadline, and description.
 *
 * @param {TitlesSectionProps} props - React props
 * @returns JSX.Element | null
 */
const TitlesSection = props => {
  const {
    headline,
    headlineTag = defaultProps.headlineTag,
    subheadline,
    description,
    textAlignment = defaultProps.textAlignment,
    iconName,
    externalMediaElement,
    size = defaultProps.size
  } = props;
  if (!headline) {
    return null;
  }
  const componentClassName = (0, _classnames.default)('pal-c-TitlesSection', {
    [`pal-c-TitlesSection__alignment--${textAlignment}`]: textAlignment,
    [`pal-c-TitlesSection__size--${size}`]: size
  });
  const headingStyle = size === 'oversized' ? 'title-lg' : 'title';
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName
  }, (externalMediaElement || iconName) && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-TitlesSection__icon"
  }, externalMediaElement && (0, _renderObject.renderObject)(externalMediaElement), iconName && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    name: iconName
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-TitlesSection__content"
  }, headline && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-TitlesSection__headline"
  }, /*#__PURE__*/_react.default.createElement(_Text.default, {
    textStyle: headingStyle,
    htmlTag: headlineTag,
    textAlign: textAlignment,
    textColor: "content-default"
  }, headline)), subheadline && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-TitlesSection__subheadline"
  }, /*#__PURE__*/_react.default.createElement(_Text.default, {
    textStyle: "body",
    htmlTag: "p",
    textAlign: textAlignment,
    textColor: "body-default"
  }, subheadline)), description && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-TitlesSection__description"
  }, /*#__PURE__*/_react.default.createElement(_Text.default, {
    textStyle: "body",
    htmlTag: "p",
    textAlign: textAlignment,
    textColor: "xsubtle"
  }, description))));
};

/**
 * Property type definitions
 * @typedef TitlesSectionProps
 * @type {object}
 * @property {string} headline - Required. Main heading text to display.
 * @property {string} [headlineTag='h2'] - Optional. HTML tag to render for the headline (e.g., 'h1', 'h2', 'h3').
 * @property {string} [subheadline] - Optional. Subheadline text rendered below the headline.
 * @property {string} [description] - Optional. Additional description text rendered below the subheadline.
 * @property {string} [textAlignment] - Optional. Text alignment style. One of `'left'` or `'center'`.
 * @property {string} [iconName] - Optional. Name of the icon to show above the headline.
 * @property {element} [externalMediaElement] - Optional. External media element to display instead of the icon.
 * @property {string} [size] - Optional. Sets the style on the heading ( 'default' sets to title and 'oversized' sets to title-lg )
 */
exports.TitlesSection = TitlesSection;
const propTypes = exports.propTypes = {
  /**
   * Main heading text
   */
  headline: _propTypes.default.string.isRequired,
  /**
   * HTML tag for the headline (e.g., h1, h2)
   */
  headlineTag: _propTypes.default.string,
  /**
   * Optional subheadline text
   */
  subheadline: _propTypes.default.string,
  /**
   * Optional description text
   */
  description: _propTypes.default.string,
  /**
   * Sets the style of the heading
   * - 'default' -  sets the heading style to title
   * - 'oversized' - sets the heading style to title-lg
   */
  size: _propTypes.default.string,
  /**
   * Text alignment. One of: 'left' | 'center'
   */
  textAlignment: _propTypes.default.string,
  /**
   * Icon name to display above the headline
   */
  iconName: _propTypes.default.string,
  /**
   * External Media Element to display instead of icon
   */
  externalMediaElement: _propTypes.default.object
};

/**
 * Default prop values
 * @type {object}
 * @property {string} headlineTag='h2' - Sets the default headlineTag to h2
 * @property {string} size='default' - Sets the default size to default
 * @property {string} textAlignment='left' - Sets the default textAlignment to left
 */
const defaultProps = exports.defaultProps = {
  headlineTag: 'h2',
  size: 'default',
  textAlignment: 'left'
};
TitlesSection.propTypes = propTypes;
var _default = exports.default = TitlesSection;